import React from 'react';
import { Loader, Pagination } from '@mantine/core';
import { formatDateTime } from '../../monitoring-page.utils';
import { TransferAuditDetail } from '../TransferAuditDetail/TransferAuditDetail';
import { MergeAuditDetail } from '../MergeAuditDetail/MergeAuditDetail';
import type { UseMonitoringPage } from '../../useMonitoringPage';
import classes from '../../monitoring-page.module.scss';

type AuditLogsTableProps = Pick<
  UseMonitoringPage,
  | 'movementRows'
  | 'movementTotalPages'
  | 'movementPage'
  | 'setMovementPage'
  | 'movementTab'
  | 'setMovementTab'
  | 'isMovementLoading'
  | 'hasLoadedMovements'
  | 'selectedMovementIndex'
  | 'openMovementDetail'
  | 'prevMovementDetail'
  | 'nextMovementDetail'
  | 'closeMovementDetail'
  | 'mergeRows'
  | 'mergeTotalPages'
  | 'mergePage'
  | 'setMergePage'
  | 'isMergeLoading'
  | 'hasLoadedMerges'
  | 'selectedMergeIndex'
  | 'openMergeDetail'
  | 'prevMergeDetail'
  | 'nextMergeDetail'
  | 'closeMergeDetail'
>;

const transferKindLabels = {
  partial_transfer: 'העברה חלקית',
  full_transfer: 'העברה מלאה',
  merge_transfer: 'העברה במיזוג',
} as const;

const getSourceTableName = (row: AuditLogsTableProps['movementRows'][number]) =>
  row.sourceOrderBefore[0]?.tableName ?? '-';

const getTargetTableName = (row: AuditLogsTableProps['movementRows'][number]) =>
  row.targetOrderAfter[0]?.tableName ?? row.targetOrderBefore[0]?.tableName ?? '-';

const mergeKindLabels: Record<string, string> = {
  merge: 'מיזוג',
  partial_split: 'פיצול חלקי',
  full_split: 'פיצול מלא',
};

export const AuditLogsTable: React.FC<AuditLogsTableProps> = ({
  movementRows,
  movementTotalPages,
  movementPage,
  setMovementPage,
  movementTab,
  setMovementTab,
  isMovementLoading,
  hasLoadedMovements,
  selectedMovementIndex,
  openMovementDetail,
  prevMovementDetail,
  nextMovementDetail,
  closeMovementDetail,
  mergeRows,
  mergeTotalPages,
  mergePage,
  setMergePage,
  isMergeLoading,
  hasLoadedMerges,
  selectedMergeIndex,
  openMergeDetail,
  prevMergeDetail,
  nextMergeDetail,
  closeMergeDetail,
}) => {
  const isTransfersLoading = movementTab === 'transfers' && isMovementLoading && !hasLoadedMovements;
  const isMergesLoading = movementTab === 'merges' && isMergeLoading && !hasLoadedMerges;

  if (isTransfersLoading || isMergesLoading) {
    return (
      <div className={classes.loadingWrap}>
        <Loader />
      </div>
    );
  }

  // Transfer detail view
  if (movementTab === 'transfers' && selectedMovementIndex !== null && movementRows[selectedMovementIndex]) {
    return (
      <TransferAuditDetail
        row={movementRows[selectedMovementIndex]}
      />
    );
  }

  // Merge detail view
  if (movementTab === 'merges' && selectedMergeIndex !== null && mergeRows[selectedMergeIndex]) {
    return (
      <MergeAuditDetail
        row={mergeRows[selectedMergeIndex]}
      />
    );
  }

  return (
    <div className={classes.sectionStack}>
      {movementTab === 'transfers' && (
        <div className={classes.tableWrap}>
          <table className={classes.table}>
            <thead>
              <tr>
                <th>{'תאריך'}</th>
                <th>{'הפעלה'}</th>
                <th>{'מלצר'}</th>
                <th>{'שולחן מקור'}</th>
                <th>{'שולחן יעד'}</th>
                <th>{'סוג העברה'}</th>
                <th>{'פריטים שהועברו'}</th>
              </tr>
            </thead>
            <tbody>
              {movementRows.map((row, index) => (
                <tr
                  key={row.id}
                  className={classes.clickableRow}
                  onClick={() => openMovementDetail(index)}
                  data-testid={`monitoring-transfers-row-${row.id}`}
                >
                  <td data-label={'תאריך'}>{formatDateTime(row.createdAt)}</td>
                  <td data-label={'הפעלה'}>{row.sessionName || row.sessionId}</td>
                  <td data-label={'מלצר'}>{row.performedByName ?? '-'}</td>
                  <td data-label={'שולחן מקור'}>{getSourceTableName(row)}</td>
                  <td data-label={'שולחן יעד'}>{getTargetTableName(row)}</td>
                  <td data-label={'סוג העברה'}>{row.transferKind ? transferKindLabels[row.transferKind] : '-'}</td>
                  <td data-label={'פריטים שהועברו'}>
                    {row.sourceOrderBefore
                      .filter((line) => row.items.includes(line.orderlineId))
                      .map((line) => line.productName ?? 'פריט כללי')
                      .join(', ') || '-'}
                  </td>
                </tr>
              ))}
              {movementRows.length === 0 && (
                <tr>
                  <td colSpan={7} className={classes.empty}>
                    {'אין העברות להצגה.'}
                  </td>
                </tr>
              )}
            </tbody>
          </table>

          {movementTotalPages > 1 && (
            <div className={classes.pagination}>
              <Pagination total={movementTotalPages} value={movementPage} onChange={setMovementPage} size="sm" />
            </div>
          )}
        </div>
      )}

      {movementTab === 'merges' && (
        <div className={classes.tableWrap}>
          <table className={classes.table}>
            <thead>
              <tr>
                <th>{'תאריך'}</th>
                <th>{'הפעלה'}</th>
                <th>{'מלצר'}</th>
                <th>{'סוג'}</th>
                <th>{'קבוצה'}</th>
                <th>{'שולחנות שפוצלו'}</th>
              </tr>
            </thead>
            <tbody>
              {mergeRows.map((row, index) => (
                <tr
                  key={row.id}
                  className={classes.clickableRow}
                  onClick={() => openMergeDetail(index)}
                  data-testid={`monitoring-merges-row-${row.id}`}
                >
                  <td data-label={'תאריך'}>{formatDateTime(row.createdAt)}</td>
                  <td data-label={'הפעלה'}>{row.sessionName || row.sessionId}</td>
                  <td data-label={'מלצר'}>{row.performedByName ?? '-'}</td>
                  <td data-label={'סוג'}>{mergeKindLabels[row.kind] ?? row.kind}</td>
                  <td data-label={'קבוצה'}>
                    {row.mergedTables.map((t) => t.tableName).join(', ') || '-'}
                  </td>
                  <td data-label={'שולחנות שפוצלו'}>
                    {(row.kind === 'partial_split' || row.kind === 'full_split')
                      ? row.mergedTables.filter((t) => !row.mergedTablesAfter.some((a) => a.tablePk === t.tablePk)).map((t) => t.tableName).join(', ') || '-'
                      : '-'}
                  </td>
                </tr>
              ))}
              {mergeRows.length === 0 && (
                <tr>
                  <td colSpan={6} className={classes.empty}>
                    {'אין מיזוגים להצגה.'}
                  </td>
                </tr>
              )}
            </tbody>
          </table>

          {mergeTotalPages > 1 && (
            <div className={classes.pagination}>
              <Pagination total={mergeTotalPages} value={mergePage} onChange={setMergePage} size="sm" />
            </div>
          )}
        </div>
      )}
    </div>
  );
};
